// Панель фильтров — общая шапка всех экранов (п.20, 2026-09-07).
// Переработана 2026-09-07 по замечанию владельца («выглядит бардаком, занимает
// много места»): панель компактная и всегда на виду (не уезжает при прокрутке,
// как и боковое меню), а её содержимое разложено по смыслу:
// Раскладка переработана владельцем 2026-09-15 — три строки и ничего скрытого:
//   1) корпуса и поле работы, а в правом верхнем углу — теги выбранного
//      («▾ N» разворачивает список, «✕» снимает все фильтры);
//   2) контуры, справа той же строки — наборы фильтров чипами и «＋»;
//   3) редкие фильтры — место, этаж, этап, подрядчик, группа работ: раньше
//      прятались за кнопкой «Ещё фильтры», теперь всегда на виду (кнопки нет).
// Подписей у полей нет (замечание владельца 2026-09-07): содержимое говорит само —
// чипы корпусов, чипы контуров, поле с подсказкой, список наборов.
// Состояние живёт в общем сторе (src/filter.ts), оболочка получает его колбэком.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ask, confirmBox } from '../dialog';
import { call, p, type Data } from '../api';
import { etazhiFiltra, filterStore, matchers, type FilterState, type WorkMeta } from '../filter';
import type { ShellCtx } from '../shell';
import { kontury } from '../util';

type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
type Spisok = Data<'/api/projects/{pid}/spiski', 'get'>[number];
type Structure = Data<'/api/projects/{pid}/structure', 'get'>;

type Preset = Data<'/api/projects/{pid}/presets', 'get'>[number];
/** дополнительные фильтры: редкие, стоят третьей строкой панели (2026-09-15) */
const DOP: [keyof FilterState, string][] = [
  ['mesto', 'Место'], ['etazh', 'Этаж'], ['etap', 'Этап работ'],
  ['podr', 'Подрядчик'], ['sistema', 'Группа работ'],
];

/** Этажи — мультивыбор галочками (владелец 2026-09-15, #91); значение — этажи через «|» */
function EtazhiVybor({ znach, vse, onSave }: { znach: string; vse: string[]; onSave: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const vybrany = etazhiFiltra(znach);
  const tgl = (e: string) => {
    const n = vybrany.includes(e) ? vybrany.filter(x => x !== e) : [...vybrany, e];
    onSave(vse.filter(x => n.includes(x)).join('|'));   // порядок — как в списке этажей
  };
  return (
    <div className="wc-multi fgrp fetazhi">
      <button type="button" className="sec wc-multi-btn" title="Этажи: можно отметить несколько"
        onClick={() => setOpen(o => !o)}>
        <span>{vybrany.length ? 'Этаж: ' + vybrany.join(', ') : 'Этаж: все'}</span>
        <span className="wc-multi-v">▾</span>
      </button>
      {open && (
        <>
          <div className="wc-multi-fon" onClick={() => setOpen(false)} />
          <div className="wc-multi-spisok">
            {vse.map(e => (
              <label key={e}>
                <input type="checkbox" checked={vybrany.includes(e)} onChange={() => tgl(e)} /> {e}
              </label>
            ))}
            {!vse.length && <div className="muted">Этажей нет</div>}
            <div className="wc-multi-niz">
              <button className="sec" onClick={() => { onSave(''); setOpen(false); }}>Все этажи</button>
              <button className="sec" onClick={() => setOpen(false)}>Готово</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** теги выбранного: по умолчанию свёрнуты в одну строку (2026-09-07) */
function loadTagsOpen(): boolean {
  try { return localStorage.getItem('plot_ftags') === '1'; } catch { return false; }
}

/** На «Этажах» панель показывает только корпуса (решение владельца 2026-09-09):
    остальные фильтры там ни на что не влияют и занимают место.
    На «Работах» (владелец 2026-09-15) — наоборот, без корпусов, этажа и подрядчика
    (справочник работ отбирается только `matchesWork`, они на него не действуют),
    а место, этап и группа работ — строками кнопок, как контуры. */
export function Filters({ ctx, tolkoKorpusa = false, dlyaRabot = false, bezRaboty = false }:
  { ctx: ShellCtx; tolkoKorpusa?: boolean; dlyaRabot?: boolean;
    /** «Модель ГПР» (владелец 2026-09-15): поле работы живёт в шапке таблицы (RabotyFiltr) */
    bezRaboty?: boolean }) {
  const [f, setF] = useState<FilterState>(filterStore.get());
  const [korpusa, setKorpusa] = useState<string[]>([]);
  const [pustye, setPustye] = useState<string[]>([]);      // корпуса без задач (2026-09-15)
  const [etazhi, setEtazhi] = useState<string[]>([]);
  const [workNames, setWorkNames] = useState<string[]>([]);
  const [spiski, setSpiski] = useState<Spisok[]>([]);
  const [sistemy, setSistemy] = useState<string[]>([]);
  const [etapy, setEtapy] = useState<string[]>([]);
  const [sug, setSug] = useState<string[]>([]);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [presetCur, setPresetCur] = useState<number | null>(null);
  const [tagsOpen, setTagsOpen] = useState<boolean>(loadTagsOpen);

  // стор — единственный источник правды; панель на него подписана. `ver` растёт на
  // каждое событие стора: пришли работы (setMeta) или стал известен список корпусов —
  // само состояние фильтра при этом прежнее, а правила отбора уже другие
  const [ver, setVer] = useState(0);
  useEffect(() => filterStore.subscribe(() => { setF(filterStore.get()); setVer(v => v + 1); }), []);
  useEffect(() => { filterStore.setProject(ctx.pid); }, [ctx.pid]);
  // при каждом изменении отдаём оболочке готовые правила отбора. Колбэк — через ref, а не
  // в зависимостях: оболочка передаёт ctx новым объектом на каждой отрисовке, и эффект
  // с [f, ctx] замыкался в круг «правила → отрисовка оболочки → новый ctx → правила»:
  // модель перерисовывалась 500–750 раз в секунду даже в покое (найдено 2026-09-18)
  const onFilterChange = useRef(ctx.onFilterChange);
  onFilterChange.current = ctx.onFilterChange;
  useEffect(() => { onFilterChange.current?.(matchers(f, filterStore.meta())); }, [f, ver]);

  const load = useCallback(async () => {
    const [w, sp, t, st] = await Promise.all([
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<Spisok[]>('get', p('/api/projects/{pid}/spiski', { pid: ctx.pid })),
      call<{ korpusa: string[]; pustye?: string[] }>('get', p('/api/projects/{pid}/tasks', { pid: ctx.pid }) + '?limit=1'),
      call<Structure>('get', p('/api/projects/{pid}/structure', { pid: ctx.pid })),
    ]);
    if (w.ok) {
      const meta: Record<string, WorkMeta> = {};
      w.data.forEach(x => { meta[x.name] = { mesto: x.mesto, etap: x.etap, sistema: x.sistema, kontur: x.kontur }; });
      filterStore.setMeta(meta);
      setWorkNames(w.data.map(x => x.name).sort((a, b) => a.localeCompare(b, 'ru')));
      setSistemy([...new Set(w.data.map(x => x.sistema).filter(Boolean) as string[])].sort());
    }
    if (sp.ok) setSpiski(sp.data);
    if (t.ok) { setKorpusa(t.data.korpusa); setPustye(t.data.pustye || []); filterStore.initKorpusa(t.data.korpusa); }
    if (st.ok) {
      const set = new Set<string>();
      Object.values(st.data).forEach(fs => fs.forEach(x => { if (x.etazh) set.add(String(x.etazh)); }));
      setEtazhi([...set].sort((a, b) => {
        const na = parseFloat(a), nb = parseFloat(b);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        if (!isNaN(na)) return -1;
        if (!isNaN(nb)) return 1;
        return a.localeCompare(b, 'ru');
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.reloadToken]);
  useEffect(() => { void load(); }, [load]);

  /** наборы фильтров: свои и общие — с сервера (2026-09-07) */
  const loadPresets = useCallback(async () => {
    const r = await call<Preset[]>('get', p('/api/projects/{pid}/presets', { pid: ctx.pid }));
    if (!r.ok) return;
    setPresets(r.data);
    // разовый перенос наборов, сохранённых в браузере до переезда на сервер
    if (!r.data.length) {
      let staryе: Record<string, FilterState> = {};
      try { staryе = JSON.parse(localStorage.getItem('plot_presets') || '{}'); } catch { /* нет */ }
      const imena = Object.keys(staryе);
      if (imena.length) {
        for (const n of imena) {
          await call('post', p('/api/projects/{pid}/presets', { pid: ctx.pid }),
            { name: n, data: staryе[n] });
        }
        try { localStorage.removeItem('plot_presets'); } catch { /* приватный режим */ }
        const again = await call<Preset[]>('get', p('/api/projects/{pid}/presets', { pid: ctx.pid }));
        if (again.ok) setPresets(again.data);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid]);
  useEffect(() => { void loadPresets(); }, [loadPresets]);
  // список этапов: из справочника и из работ
  useEffect(() => {
    const set = new Set(spiski.filter(x => x.kod === 'etap').map(x => x.znachenie).filter(Boolean));
    Object.values(filterStore.meta()).forEach(m => { if (m.etap) set.add(m.etap); });
    setEtapy([...set].sort());
  }, [spiski, workNames]);

  const opts = (kod: string) => spiski.filter(x => x.kod === kod).map(x => x.znachenie).filter(Boolean);
  const gruppy = [...new Set([...opts('gruppa'), ...sistemy])].sort((a, b) => a.localeCompare(b, 'ru'));
  // контур без единой работы кнопкой не показываем (владелец 2026-09-15); выбранный — оставляем,
  // чтобы его можно было снять
  const konturyRabot = new Set(Object.values(filterStore.meta())
    .flatMap(m => kontury(m.kontur, opts('kontur'))).map(x => x.toLowerCase()));
  const set = (patch: Partial<FilterState>) => filterStore.set(patch);

  function clickKorpus(k: string, e: React.MouseEvent) {
    // обычный клик — только этот корпус, Ctrl+клик — добавить/убрать
    if (e.ctrlKey || e.metaKey) {
      set({ korpusa: f.korpusa.includes(k) ? f.korpusa.filter(x => x !== k) : [...f.korpusa, k] });
    } else set({ korpusa: [k] });
  }
  function clickKontur(k: string) {
    set({ kontury: f.kontury.includes(k) ? f.kontury.filter(x => x !== k) : [...f.kontury, k] });
  }
  function typeWork(v: string) {
    set({ name: v });
    const q = v.trim().toLowerCase();
    setSug(workNames.filter(n => !f.works.includes(n) && (!q || n.toLowerCase().includes(q))).slice(0, 15));
  }
  function pickWork(n: string) {
    const works = [...f.works, n];
    // набранное остаётся в поле, список — тот же: можно выбрать «…1 сторона»,
    // потом «…2 сторона» (замечание владельца 2026-09-07)
    set({ works });
    const q = f.name.trim().toLowerCase();
    setSug(workNames.filter(x => !works.includes(x)
      && (!q || x.toLowerCase().includes(q))).slice(0, 15));
  }
  function toggleTags() {
    setTagsOpen(v => {
      const next = !v;
      try { localStorage.setItem('plot_ftags', next ? '1' : ''); } catch { /* приватный режим */ }
      return next;
    });
  }

  async function savePreset() {
    const name = (await ask('Название набора фильтров:') || '').trim();
    if (!name) return;
    // общий набор виден всем участникам проекта; задаёт его администратор
    const obshchij = ctx.role === 'администратор'
      && await confirmBox('Сделать набор «' + name + '» общим для всех пользователей проекта?'
        + '\n\nОК — общий (виден всем), Отмена — только мой.');
    const r = await call<{ id: number; sistemnyj: boolean }>('post',
      p('/api/projects/{pid}/presets', { pid: ctx.pid }), { name, data: f, sistemnyj: obshchij });
    if (!r.ok) { ctx.onError?.(r.error.message); return; }
    setPresetCur(r.data.id);
    ctx.onSaved?.('Набор фильтров «' + name + '»' + (obshchij ? ' (общий)' : '') + ' сохранён.');
    await loadPresets();
  }
  function applyPreset(pr: Preset) {
    setPresetCur(pr.id);
    const st = { ...(pr.data as FilterState) };
    filterStore.set({ ...st, korpusa: (st.korpusa || []).filter(k => korpusa.includes(k)) });
  }
  async function delPreset(pr: Preset) {
    if (!await confirmBox('Удалить набор фильтров «' + pr.name + '»'
      + (pr.sistemnyj ? ' (общий, пропадёт у всех)' : '') + '?')) return;
    const r = await call('delete', '/api/presets/' + pr.id);
    if (!r.ok) { ctx.onError?.(r.error.message); return; }
    if (presetCur === pr.id) setPresetCur(null);
    await loadPresets();
  }

  // строка «Отфильтровано»: что выбрано и как это снять
  const chips: [string, () => void][] = [];
  if (!dlyaRabot) {
    f.korpusa.forEach(k => chips.push(['Корпус: ' + k, () => set({ korpusa: f.korpusa.filter(x => x !== k) })]));
  }
  if (!tolkoKorpusa) {
  f.kontury.forEach(k => chips.push(['Контур: ' + k, () => set({ kontury: f.kontury.filter(x => x !== k) })]));
  if (!bezRaboty) {   // на «Модели» выбранные работы — тегами в шапке таблицы
    f.works.forEach(n => chips.push(['Работа: ' + n, () => set({ works: f.works.filter(x => x !== n) })]));
    if (f.name && !f.works.length) chips.push(['Название: «' + f.name + '»', () => set({ name: '' })]);
  }
  DOP.forEach(([k, lab]) => {
    if (dlyaRabot && (k === 'etazh' || k === 'podr')) return;   // на «Работах» не действуют
    const v = f[k] as string;
    if (k === 'etazh') {   // мультивыбор — тег на каждый этаж (#91)
      etazhiFiltra(v).forEach(e => chips.push(['Этаж: ' + e,
        () => set({ etazh: etazhiFiltra(v).filter(x => x !== e).join('|') })]));
      return;
    }
    if (v) chips.push([lab + ': ' + v, () => set({ [k]: '' } as Partial<FilterState>)]);
  });
  }

  // подписей у фильтров нет (замечание владельца 2026-09-07): название поля
  // стоит первой строкой самого списка — «Место: все»
  const sel = (k: keyof FilterState, label: string, list: string[]) => (
    <select className="fgrp" key={k} title={label} value={f[k] as string}
      onChange={e => set({ [k]: e.target.value } as Partial<FilterState>)}>
      <option value="">{label}: все</option>
      {list.map(v => <option key={v}>{v}</option>)}
    </select>
  );

  // наборы фильтров — справа в строке редких фильтров (владелец 2026-09-15: вторая строка
  // панели ушла, контуры переехали к корпусам); на «Работах» — в строке «Группа работ»
  const nabory = (
    <span className="fkt fkt-line fpresets">
      {presets.map(pr => (
        <a key={pr.id} className={'ktag' + (presetCur === pr.id ? ' on' : '')}
          title={(pr.sistemnyj ? 'Общий набор (виден всем): ' : 'Мой набор: ') + pr.name}
          onClick={() => applyPreset(pr)}>
          {pr.sistemnyj && <b className="fobshch" title="Общий набор">★</b>}{pr.name}
          {(pr.moy || (pr.sistemnyj && ctx.role === 'администратор')) && (
            <span className="fdel" title="Удалить набор"
              onClick={e => { e.stopPropagation(); void delPreset(pr); }}>×</span>
          )}
        </a>
      ))}
      <a className="tagadd" title="Сохранить текущие фильтры как набор"
        onClick={() => void savePreset()}>＋</a>
    </span>
  );

  return (
    <>
    <div className="fpanel">
      {/* 1. корпуса и название работы — одной строкой: корпусов может быть 15,
             а может быть два, и тогда строка не пропадает зря (2026-09-07) */}
      <div className="frow frow-line">
        {!dlyaRabot && <div className="fgrp fgrp-k">
          <span className="fkt fkt-line">
            <a className={'ktag' + (f.korpusa.length ? '' : ' on')}
              title="Показывать все корпуса" onClick={() => set({ korpusa: [] })}>все</a>
            {/* корпус без задач кнопкой не показываем (владелец 2026-09-15), кроме «Этажей»:
                там заводят новый корпус, у которого задач ещё нет */}
            {korpusa.filter(k => tolkoKorpusa || !pustye.includes(k) || f.korpusa.includes(k)).map(k => (
              <a key={k} className={'ktag' + (f.korpusa.includes(k) ? ' on' : '')}
                title={'Клик — только ' + k + '; Ctrl+клик — добавить к выбранным'}
                onClick={e => clickKorpus(k, e)}>{k}</a>
            ))}
          </span>
        </div>}

        {/* контуры — в одной строке с корпусами (владелец 2026-09-15) */}
        {!tolkoKorpusa && <div className="fgrp fgrp-kont" title="Контуры работ">
          <span className="fkt fkt-line">
            <a className={'ktag' + (f.kontury.length ? '' : ' on')}
              title="Показывать все контуры" onClick={() => set({ kontury: [] })}>все</a>
            {opts('kontur').filter(k => konturyRabot.has(k.toLowerCase()) || f.kontury.includes(k)).map(k => (
              <a key={k} className={'ktag' + (f.kontury.includes(k) ? ' on' : '')}
                onClick={() => clickKontur(k)}>{k}</a>
            ))}
          </span>
        </div>}

        {!tolkoKorpusa && !bezRaboty && <div className="fgrp fgrp-grow">
          <div className="wbox">
            <input placeholder="Название работы — начните вводить…"
              autoComplete="off" value={f.name}
              onChange={e => typeWork(e.target.value)}
              onFocus={() => typeWork(f.name)}
              onBlur={() => window.setTimeout(() => setSug([]), 150)}
              onKeyDown={e => {
                if (e.key === 'Enter' && sug.length) { e.preventDefault(); pickWork(sug[0]); }
                else if (e.key === 'Escape') setSug([]);
                else if (e.key === 'Backspace' && !f.name && f.works.length)
                  set({ works: f.works.slice(0, -1) });
              }} />
            {sug.length > 0 && (
              <div className="wsug" style={{ display: 'block' }}>
                {sug.map((n, i) => (
                  <a key={n} className={i === 0 ? 'hot' : undefined}
                    onMouseDown={e => { e.preventDefault(); pickWork(n); }}>{n}</a>
                ))}
              </div>
            )}
          </div>
        </div>}

        {/* выбранные фильтры — тегами в правом верхнем углу панели (владелец
            2026-09-15; раньше лежали отдельной полосой под ней и съедали строку).
            Свёрнуты в одну строку, «▾ N» разворачивает, «✕» снимает все. */}
        {/* на «Модели ГПР» выбранные фильтры — тегами в окне шапки таблицы (RabotyFiltr) */}
        {chips.length > 0 && !bezRaboty && (
          <div className={'ftags-ug' + (tagsOpen ? ' open' : '')}>
            <span className={'ftags' + (tagsOpen ? '' : ' one')}>
              {chips.map(([label, off], i) => (
                <span className="ftag" key={i}>{label}
                  <a title="Убрать этот фильтр" onClick={off}>×</a>
                </span>
              ))}
            </span>
            <button className="sec fclear" onClick={toggleTags}
              title={tagsOpen ? 'Свернуть теги в одну строку' : 'Показать все выбранные фильтры'}>
              {tagsOpen ? '▴' : '▾ ' + chips.length}
            </button>
            <button className="sec fclear" title="Снять все фильтры" aria-label="Снять все фильтры"
              onClick={() => { filterStore.reset(); setPresetCur(null); }}>✕</button>
          </div>
        )}
      </div>

      {/* 2. контуры и наборы фильтров одной строкой: названия контуров длинные
             («Слаботочные сети»), поэтому чипы прокручиваются внутри себя,
             а наборы стоят справа (владелец 2026-09-15) */}

      {/* 3. редкие фильтры — всегда на виду третьей строкой (владелец 2026-09-15):
             кнопки «Ещё фильтры» больше нет, прятать пять списков не за чем */}
      {!tolkoKorpusa && !dlyaRabot && <div className="frow frow-dop">{DOP.map(([k, lab]) => (k === 'etazh'
        ? <EtazhiVybor key="etazh" znach={f.etazh} vse={etazhi} onSave={v => set({ etazh: v })} />
        : sel(k, lab, k === 'mesto' ? opts('mesto')
          : k === 'etap' ? etapy : k === 'podr' ? opts('podryadchik') : gruppy)))}
        {nabory}
      </div>}

      {/* 3'. на «Работах» (владелец 2026-09-15): место, этап и группа — кнопками, как
             контуры; одно значение на строку, повторный клик или «все» снимает */}
      {dlyaRabot && ([['mesto', 'Место', opts('mesto')], ['etap', 'Этап', etapy],
        ['sistema', 'Группа работ', gruppy]] as ['mesto' | 'etap' | 'sistema', string, string[]][])
        .map(([k, lab, list]) => (
          <div className="frow frow-line frow-kont" key={k}>
            <div className="fgrp fgrp-kont fgrp-wide">
              <span className="fkt fkt-line">
                <a className={'ktag' + (f[k] ? '' : ' on')} title={lab + ': показывать все'}
                  onClick={() => set({ [k]: '' } as Partial<FilterState>)}>{lab}: все</a>
                {list.map(v => (
                  <a key={v} className={'ktag' + (f[k] === v ? ' on' : '')}
                    onClick={() => set({ [k]: f[k] === v ? '' : v } as Partial<FilterState>)}>{v}</a>
                ))}
              </span>
            </div>
            {k === 'sistema' && nabory}
          </div>
        ))}

    </div>
    </>
  );
}
